// Language resolution rules for a new (not-yet-authenticated) visitor, in
// priority order:
//   1. Honor the first supported language in the browser's ordered
//      preference list — this mirrors standard Accept-Language negotiation,
//      which is how the backend (and toast messages) already resolve
//      language, so the UI and toasts stay consistent (see DAT-1569).
//   2. Otherwise fall back to the preferred language.
//
// The preferred language mirrors the backend default and is supplied by the
// caller (see locales.ts), which is where the environment is known:
//   - Production  → French  (Datizy is a French-first product)
//   - Other envs  → English
// PREFERRED_LANGUAGE below is the production default.

export const PREFERRED_LANGUAGE = 'fr';

// Read the browser's ordered language preferences in a non-browser-safe way.
export function getBrowserLanguages(): string[] {
  if (typeof navigator === 'undefined') return [];

  const nav: any = navigator as any;
  if (Array.isArray(nav.languages) && nav.languages.length) {
    return nav.languages as string[];
  }
  if (nav.language) return [String(nav.language)];
  return [];
}

// Pure resolver — keep free of browser globals so it stays unit-testable.
export function resolveLanguage(
  langs: string[],
  supportedLanguages: string[],
  preferredLanguage: string = PREFERRED_LANGUAGE,
): string {
  // Normalize: try full tag (e.g., "en-US") then base ("en")
  const candidates = langs
    .filter(Boolean)
    .map((l) => String(l).toLowerCase())
    .flatMap((l) => [l, l.split('-')[0]]);

  // Top-preference-wins: honor the first supported language in the browser's
  // ordered preference list, same as standard Accept-Language negotiation.
  const match = candidates.find((l) => supportedLanguages.includes(l));
  return match || preferredLanguage;
}
